export enum VariationKey {
  ORIGINAL = 'original-page',
  VARIATION_1 = 'variation-1',
}

/**
 * Annual-plan conversion A/B (LC-28827) variation keys. Unlike legacy experiences — whose
 * variations are resolved from the Convert `variation.key` — this newer experience only exposes
 * the variation `id`, so it is resolved to its own key set (see `ANNUAL_PLAN_VARIATION_ID_TO_KEY`).
 */
export enum AnnualPlanVariationKey {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

/** A variation key resolved for any experience, legacy or newer. */
export type ResolvedVariationKey = VariationKey | AnnualPlanVariationKey;

export enum VariationName {
  ORIGINAL = 'original-page',
  DEFAULT_CORE_PLAN = 'default-core-plan',
  PLAN_VIEW_1 = 'plan-view-1',
  PLAN_VIEW_2 = 'plan-view-2',
  CORE_PLAN_ZERO_TAG = 'core-plan-zero-tag',
}

export const VARIATION_NAME_TO_KEY_MAP = {
  [VariationName.DEFAULT_CORE_PLAN]: VariationKey.VARIATION_1,
  [VariationName.PLAN_VIEW_1]: VariationKey.VARIATION_1,
  [VariationName.PLAN_VIEW_2]: VariationKey.VARIATION_1,
  [VariationName.CORE_PLAN_ZERO_TAG]: VariationKey.VARIATION_1,
  [VariationName.ORIGINAL]: VariationKey.ORIGINAL,
} as const satisfies Record<VariationName, VariationKey>;

export enum ExperienceMap {
  TEST = '1004184758',
  CORE_PLAN_AS_DEFAULT = '1004185286',
  CORE_PLAN_ZERO_TAG = '1004196402',
  /** Annual-plan conversion A/B test (LC-28827). */
  ANNUAL_PLAN = '1004211841',
}

/** Convert experience id for the annual-plan conversion A/B test (LC-28827). */
export const ANNUAL_PLAN_EXPERIENCE_ID = ExperienceMap.ANNUAL_PLAN;

/** Convert variation ids for the annual-plan A/B: control ("Original Page") / treatment ("Variation 1"). */
export const ANNUAL_PLAN_CONTROL_VARIATION_ID = '1004497482';
export const ANNUAL_PLAN_TREATMENT_VARIATION_ID = '1004497483';

/**
 * The annual-plan experience is a newer Convert experience whose variation carries no legacy
 * `key`, so its variation is resolved from the variation id instead (see `parseExperiments`).
 */
export const ANNUAL_PLAN_VARIATION_ID_TO_KEY = {
  [ANNUAL_PLAN_CONTROL_VARIATION_ID]: AnnualPlanVariationKey.CONTROL,
  [ANNUAL_PLAN_TREATMENT_VARIATION_ID]: AnnualPlanVariationKey.TREATMENT,
} as const satisfies Record<string, AnnualPlanVariationKey>;

export type RawConvertVariationKey = `${string}-${VariationKey}`;
export interface RawConvertVariation {
  id?: string;
  key?: RawConvertVariationKey;
  name?: string;
}

interface RawConvertExperienceData {
  variation?: RawConvertVariation;
}

export interface RawConvertData {
  experiences?: Partial<Record<ExperienceMap, RawConvertExperienceData>>;
}

export interface ExperienceResult {
  id: ExperienceMap;
  variationKey: ResolvedVariationKey;
}

declare global {
  interface Window {
    convert?: {
      currentData?: RawConvertData;
      data?: RawConvertData;
    };
  }
}
